import React, { useCallback } from 'react';
import { isNil } from 'lodash';
import cx from 'classnames';
import { useHistory } from 'react-router-dom';
import { useResourceDoc } from 'new/hooks/adapters/useResourceDoc';
import { RESOURCE_TYPE_FLOW } from 'js/constants/resourceTypes';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import DocEditor from 'js/components/elements/editors/DocEditor';
import { GET_PIPELINE_DOC_REQUEST } from 'js/constants/actionTypes';
import styles from './Overview.module.scss';
import SourcePipelineContainer from 'js/components/pipelines/SourcePipelineContainer';
import usePipelinesAdapter from 'new/state/pipelines/adapter/usePipelinesAdapter';
import { useFlowMetricsSync } from 'new/state/metrics/useFlowMetricsSync';
import FlowOverviewMetrics from 'new/elements/FlowDailyMetrics/FlowOverviewMetrics';

interface FlowsProps {
  resource: any;
  onFlowItemClick: (type: string, data: any) => void;
}

export const FlowOverview = (props: FlowsProps) => {
  const { resource, onFlowItemClick } = props;
  const history = useHistory();
  const doc = useResourceDoc(RESOURCE_TYPE_FLOW, resource?.flow_node_id);
  const isLoadingDoc = useIsLoading(GET_PIPELINE_DOC_REQUEST);
  const { refreshPipelineById } = usePipelinesAdapter();

  const onItemClick = useCallback(
    (type, data) => {
      onFlowItemClick(type, data);
      const params = new URLSearchParams();
      params.set('type', type);
      params.set('id', data.id);
      history.replace({
        pathname: history.location.pathname,
        search: params.toString(),
      });
    },
    [onFlowItemClick],
  );

  // This DAG renders in isPreview mode (FlowNodes' own metrics fetch is off), so
  // drive metrics here: fetch on mount + on interval-toggle, plus a stable
  // refresh for the toolbar button. Self-contained so it works wherever the flow
  // Overview is shown (Resource Triage, Resources 360), not just where a parent
  // happens to fetch metrics.
  const { refresh: refreshMetrics } = useFlowMetricsSync(
    resource?.flow_node_id,
    resource?.data_source_id,
  );

  const onRefreshPipeline = useCallback(() => {
    if (resource?.flow_node_id) {
      refreshPipelineById(Number(resource.flow_node_id));
      refreshMetrics();
    }
  }, [resource?.flow_node_id, refreshPipelineById, refreshMetrics]);

  return (
    <div
      className={cx(
        styles.overviewContainer,
        styles.flowOverviewContainer,
        'detail-view',
        !isNil(doc) && !isLoadingDoc ? styles.hasDoc : '',
      )}
    >
      <div className="tab-content">
        {!isNil(doc) && !isLoadingDoc ? (
          <DocEditor data={resource} doc={doc} readOnly isLoading={isLoadingDoc} />
        ) : null}
        <div className={styles.pipelinePreview}>
          <SourcePipelineContainer
            isPreview
            dataSource={resource}
            onItemClick={onItemClick}
            scaleFactor={1}
            refreshPipeline={onRefreshPipeline}
          />
        </div>
        <FlowOverviewMetrics flow={resource} />
      </div>
    </div>
  );
};
